import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** Dropdown with inline search. Options: { value, label, sub? } */
export default function SearchableSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  required = false,
  emptyMessage = 'No options found.',
  searchPlaceholder = 'Search…',
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef(null);
  const buttonRef    = useRef(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Decide whether dropdown should open upward (flip) to avoid viewport overflow
  const handleOpen = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = Math.min(filtered.length * 44 + 48, 260); // estimate
      setDropUp(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    }
    setOpen((v) => !v);
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
      )}
      <div className="relative">
        {/* Trigger button */}
        <button
          ref={buttonRef}
          id={id}
          type="button"
          disabled={disabled}
          onClick={handleOpen}
          className={cn(
            'flex min-h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            open && 'ring-2 ring-ring ring-offset-2',
            !selected && 'text-muted-foreground'
          )}
        >
          {/* Selected value — label on first line, sub on second (no overflow) */}
          <span className="flex-1 min-w-0 text-left">
            {selected ? (
              <span className="flex flex-col gap-0">
                <span className="truncate font-medium text-foreground leading-snug">
                  {selected.label}
                </span>
                {selected.sub && (
                  <span className="truncate text-xs text-muted-foreground leading-snug">
                    {selected.sub}
                  </span>
                )}
              </span>
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </span>
          <ChevronDown
            className={cn(
              'ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150',
              open && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown panel */}
        {open && (
          <div className={cn(
            'absolute z-[200] w-full rounded-md border bg-popover shadow-lg',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          )}>
            {/* Search input */}
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <input
                autoFocus
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground min-w-0"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Options list */}
            <div className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">{emptyMessage}</p>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o.value)}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2 text-sm transition-colors hover:bg-muted/60 text-left',
                      value === o.value && 'bg-primary/10 font-medium'
                    )}
                  >
                    <span className="truncate w-full">{o.label}</span>
                    {o.sub && (
                      <span className="truncate w-full text-xs text-muted-foreground">{o.sub}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
